import { inject as service } from '@ember/service';
import AdaptiveStore from 'ember-simple-auth/session-stores/adaptive';

/**
 * Application session store.
 *
 * Extends ember-simple-auth's Adaptive store so normal persistence is
 * unchanged, but when garaje is embedded in the carapace shell (i.e. running
 * inside an iframe) and has no session persisted on its own origin, it
 * reconstructs the session record from the shared `.envoy.christmas` auth
 * cookies.
 *
 * Why: after a user logs in through the carapace shell
 * (carapace.envoy.christmas), the auth cookies (access_token / csrf_token /
 * expires_at) are set on the shared eTLD+1, so garaje's requests are already
 * authorized and the authenticator's `_validate()` already accepts the
 * `csrf_token` cookie. But ember-simple-auth only calls the authenticator's
 * `restore()` when a session *record* exists in this store, and localStorage
 * is per-origin — carapace (a different origin) cannot create that record on
 * dashboard.envoy.christmas. Synthesizing the trivial record here (just
 * authenticator + expires_at — no secret) lets `restore()` run and validate
 * off the shared cookie, so the embedded iframe no longer drops to garaje's
 * own login page.
 *
 * Gated to the embedded case (`window.parent !== window`): a standalone
 * garaje visit keeps its normal boot, so a direct, unauthenticated visit to
 * dashboard.envoy.christmas still shows login as before.
 */
export default AdaptiveStore.extend({
  cookies: service(),

  restore() {
    return this._super(...arguments).then((data) => {
      data = data || {};

      // A real, locally-persisted session always wins — never override it.
      if (data.authenticated && data.authenticated.authenticator) {
        return data;
      }

      // Only reconstruct when framed by the carapace shell.
      if (window.parent === window) {
        return data;
      }

      const csrfToken = this.cookies.read('csrf_token');
      const expiresAtSeconds = this.cookies.read('expires_at');
      const expiresAtMs = Number(expiresAtSeconds) * 1000;

      // Require the shared session cookies and a future expiry. If the cookies
      // are missing or stale we fall through to the normal unauthenticated
      // boot; if they turn out to be invalid server-side, garaje's first
      // authorized request 401s and ember-simple-auth invalidates the session.
      if (!csrfToken || !expiresAtSeconds || !(expiresAtMs > Date.now())) {
        return data;
      }

      return {
        authenticated: {
          authenticator: 'authenticator:application',
          expires_at: expiresAtMs,
        },
      };
    });
  },
});
